"use client"

import { useEffect } from "react"
import { Link } from "@/i18n/routing"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Home, RefreshCw, AlertTriangle, ArrowLeft, ArrowRight } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"
import { localeMetadata, routing, type AppLocale } from "@/i18n/routing"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useTranslations("page.error");
  const locale = useLocale()
  const currentLocale =
    localeMetadata[locale as AppLocale] ??
    localeMetadata[routing.defaultLocale as AppLocale]
  
  useEffect(() => {
    // Log the error to an error reporting service
    console.error("Page error:", error)
  }, [error])

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="mx-auto max-w-3xl w-full">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-12 w-12 text-red-600" />
          </div>
          <h1 className="mb-4 text-3xl font-bold text-teal-900">
            {t("title")}
          </h1>
          <p className="mx-auto max-w-lg text-lg text-gray-600">
            {t("description")}
          </p>
        </div>

        <Card className="border-red-100 bg-red-50/50 mb-8">
          <CardContent className="p-6 text-start">
            <h3 className="font-semibold text-red-900 mb-2">
              {t("details_title")}
            </h3>
            <p className="text-sm text-red-700 bg-red-100 p-3 rounded font-mono whitespace-pre-wrap wrap-break-word">
              {error.message || t("global.unknown_error")}
            </p>
            {error.stack && (
              <div className="mt-4">
                <h4 className="font-semibold text-red-900 mb-2">
                  {t("stack_trace")}
                </h4>
                <pre className="text-xs text-red-700 bg-red-100 p-3 rounded font-mono whitespace-pre-wrap wrap-break-word overflow-auto max-h-60">
                  {error.stack}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center mb-8">
          <Button onClick={reset} size="lg" className="bg-teal-600 hover:bg-teal-700">
            <RefreshCw className="me-2 h-5 w-5" />
            {t("global.try_again")}
          </Button>
          <Link href="/">
            <Button variant="outline" size="lg" className="border-teal-600 text-teal-600 hover:bg-teal-50">
              <Home className="me-2 h-5 w-5" />
              {t("global.home_page")}
            </Button>
          </Link>
        </div>

        <Button variant="ghost" onClick={() => window.history.back()} className="text-teal-600 hover:bg-teal-50">
          {currentLocale.isRtl ? (
            <ArrowRight className="me-2 h-4 w-4" />
          ) : (
            <ArrowLeft className="me-2 h-4 w-4" />
          )}
          {t("back_button")}
        </Button>
      </div>
    </div>
  )
}
